import { Bot, Code2, type LucideIcon, MousePointerClick, Plug } from 'lucide-react';
import type { IntakeChannel } from '@/lib/api-types.gen';

/** How each surface a submission can arrive through is drawn: the glyph and
 *  the word beside it. Keyed by the generated union, so a channel the
 *  orchestrator gains fails this build until it is given a face here rather
 *  than quietly rendering as a bare token. The glyphs are the ones the API
 *  docs already use for the same things: code for a programmatic caller, a
 *  bot for an agent. */
const CHANNELS: Record<IntakeChannel, { label: string; Icon: LucideIcon }> = {
  manual: { label: 'Manual', Icon: MousePointerClick },
  api: { label: 'API', Icon: Code2 },
  mcp: { label: 'MCP', Icon: Plug },
  a2a: { label: 'A2A', Icon: Bot },
};

const KNOWN = new Set<string>(Object.keys(CHANNELS));

function isKnown(channel: string): channel is IntakeChannel {
  return KNOWN.has(channel);
}

/** Which integration created a submission, beside where it ran.
 *
 *  Absent on a row older than the column: nothing is drawn, because "not
 *  recorded" is not any one of the four and drawing one would be a claim the
 *  platform never made. A token this build has never heard of - an
 *  orchestrator newer than the SPA - is shown as itself, without a glyph,
 *  rather than dropped. */
export function IntakeChannelTag({ channel }: { channel?: string | null }) {
  if (!channel) return null;
  if (!isKnown(channel)) {
    return <span className="text-muted text-xs">{channel}</span>;
  }
  const { label, Icon } = CHANNELS[channel];
  return (
    <span className="text-muted text-xs inline-flex items-center gap-1.5" title={`Submitted via ${label}`}>
      <Icon size={14} aria-hidden="true" />
      {label}
    </span>
  );
}
